import React from "react";
import PropTypes from "prop-types";
import {
  Box, Card, CardHeader, Grid, CardContent,
  Divider, Typography, Link, IconButton, Tooltip
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { REACT_APP_MAP_SERVER_URL as MAP_SERVER_URL } from "../utils/env";

const getMapServerEndpoint = (url = "") => {
  const trimmedUrl = url.trim().replace(/\/$/, "");
  return trimmedUrl.endsWith("/cgi-bin/mapserv")
    ? trimmedUrl
    : `${trimmedUrl}/cgi-bin/mapserv`;
};

const MAP_SERVER_ENDPOINT = getMapServerEndpoint(MAP_SERVER_URL);

const normalizeTifName = (name = "") => {
  const trimmedName = name.trim();
  return trimmedName.endsWith(".tif") ? trimmedName : `${trimmedName}.tif`;
};

const buildTifCapabilitiesUrl = (layerName) => {
  const tifName = normalizeTifName(layerName);
  const params = new URLSearchParams({
    map: "/data/mapfile.map",
    SERVICE: "WMS",
    REQUEST: "GetCapabilities",
    VERSION: "1.3.0",
    TIFF_NAME: tifName,
  });

  return `${MAP_SERVER_ENDPOINT}?${params.toString()}`;
};

const buildTifDownloadUrl = (layerName) => {
  const tifName = normalizeTifName(layerName);
  return `${MAP_SERVER_ENDPOINT.replace(/\/cgi-bin\/mapserv$/, "")}/tifs/${encodeURIComponent(tifName)}`;
};

export default function RegionInfo({ title, data = {}, layers = [], ...other }) {
  const InfoItem = ({ label, value, isLink, linkUrl }) => (
    <Box sx={{ mb: 1 }}>
      <Typography variant="body2">
        <Typography component="span" variant="body2" sx={{ fontWeight: "bold", color: "rgba(0, 0, 0, 0.8)" }}>
          {label}:
        </Typography>{" "}
        {isLink ? (
          <Link
            href={linkUrl || "https://quantifarmtoolkit.eu/results.html"}
            target="_blank"
            rel="noopener noreferrer"
          >
            {value}
          </Link>
        ) : value}
      </Typography>
    </Box>
  );

  return (
    <Card sx={{ position: "sticky", top: 0 }} {...other}>
      <CardHeader
        title={title}
        sx={{
          bgcolor: "#DBD7D2",
          height: "3em",
          "& .MuiCardHeader-content": { my: 0 }
        }}
      />
      <Box sx={{ display: "flex", flexDirection: "row" }}>

        {/* ── Section 1: basic region fields ── */}
        <CardContent sx={{ flex: 1.3, py: 1, "&:last-child": { pb: 1 } }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <InfoItem label="Country" value={data.Country || "-"} />
              <InfoItem label="Region" value={data.LAU_NAME || "-"} />
              <InfoItem label="Crop" value={data.cropType || "-"} />
            </Grid>
            <Grid item xs={6}>
              <InfoItem label="Area (ha)" value={data.AREA_HA?.toFixed(1) || "-"} />
              <InfoItem label="Area (km²)" value={data.AREA_KM2?.toFixed(2) || "-"} />
              <InfoItem
                label="Test Case"
                value={data.TC_number || "-"}
                isLink={true}
              />
            </Grid>
          </Grid>
        </CardContent>

        <Divider orientation="vertical" flexItem />

        {/* ── Section 2: extended info ── */}
        <CardContent sx={{ flex: 1.2, py: 1, "&:last-child": { pb: 1 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InfoItem label="Biogeographical Region" value={data["Biogeographical Region"] || "-"} />
              <InfoItem label="Digital Solution" value={data["Digital Solution"] || "-"} />
              <InfoItem
                label="Typical Irrigation Needs"
                value={
                  data["Irrigation typical needs"] ? (
                    <>
                      {data["Irrigation typical needs"].split("|")[0]}
                      <a
                        href={data["Irrigation typical needs"].split("|")[1]?.replace(" source: ", "")}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {" (source)"}
                      </a>
                    </>
                  ) : "-"
                }
              />
            </Grid>
          </Grid>
        </CardContent>

        {/* ── Section 3: layers ── */}
        <Divider orientation="vertical" flexItem />
        <CardContent sx={{ flex: 0.8, py: 1, "&:last-child": { pb: 1 } }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", color: "rgba(0, 0, 0, 0.8)", mb: 1 }}
          >
            Layers
          </Typography>
          {layers.length === 0 ? (
            <InfoItem label="Layer 1" value="-" />
          ) : (
            layers.map((layerName, index) => {
              const capabilitiesUrl = buildTifCapabilitiesUrl(layerName);
              const tifName = normalizeTifName(layerName);
              const downloadUrl = buildTifDownloadUrl(layerName);

              return (
                <Box key={layerName} sx={{ mb: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="body2" sx={{ minWidth: 0 }}>
                      <Typography component="span" variant="body2" sx={{ fontWeight: "bold", color: "rgba(0, 0, 0, 0.8)" }}>
                        {`Layer ${index + 1}:`}
                      </Typography>{" "}
                      <Link
                        href={capabilitiesUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={capabilitiesUrl}
                        underline="hover"
                      >
                        {layerName.replace(".tif", "")}
                      </Link>
                    </Typography>
                    <Tooltip title={`Download ${tifName}`}>
                      <IconButton
                        component="a"
                        href={downloadUrl}
                        download={tifName}
                        size="small"
                        aria-label={`Download ${tifName}`}
                        sx={{ color: "primary.main", p: 0.25 }}
                      >
                        <FileDownloadOutlinedIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })
          )}
        </CardContent>

      </Box>
    </Card>
  );
}

RegionInfo.propTypes = {
  title: PropTypes.string,
  data: PropTypes.object,
  layers: PropTypes.arrayOf(PropTypes.string),
};
